import { Link, useParams } from 'react-router-dom';
import { Activity, ArrowLeft, Flame, Target, Trophy, Users, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { GuidedEmpty } from '../components/GuidedEmpty';
import { ModuleHero } from '../components/ModuleHero';
import { PublicEventSection } from '../components/PageTemplates';
import { useStore } from '../hooks/useStore';
import { useActiveTournament } from '../state/TournamentContext';
import { buildDeterministicEventAnalytics, type PlayerAnalyticsSummary } from '../services/deterministicAnalyticsService';

export function PublicPlayerProfilePage() {
  const { playerId = '' } = useParams<{ playerId: string }>();
  const db = useStore((row) => row);
  const { tournament } = useActiveTournament();
  const analytics = useMemo(() => buildDeterministicEventAnalytics(db, tournament.id), [db, tournament.id]);
  const player = analytics?.players.find((row) => row.playerId === playerId);

  if (!player || !analytics) {
    return (
      <GuidedEmpty
        icon={Users}
        title="Player not found"
        body="This player is not attached to the current public event."
        action={{ to: `/events/${tournament.slug}/stats`, label: 'Back to stats' }}
      />
    );
  }

  return (
    <PublicEventSection>
    <div className="public-profile">
      <Link className="btn btn--ghost btn--sm" to={`/events/${tournament.slug}/stats`}>
        <ArrowLeft size={14} /> Back to stats
      </Link>

      <ModuleHero
        eyebrow={tournament.format === 'season' ? 'Season player' : 'Event player'}
        title={player.playerName}
        description={`${player.teamNames.join(', ') || 'Unassigned'} - ${player.goals} goals, ${player.assists} assists, ${player.points} points.`}
        icon={Users}
        accent="goal"
        status={{ label: `${player.gamesPlayed} GP`, tone: player.gamesPlayed > 0 ? 'ok' : 'info' }}
      />

      <section className="public-stats__kpis">
        <StatTile label="Points" value={player.points} detail={`${player.goals}G ${player.assists}A`} />
        <StatTile label="Goal pace" value={player.gamesPlayed > 0 ? formatNumber(player.goals / player.gamesPlayed) : '-'} detail="Goals per game" />
        <StatTile label="Current streak" value={player.streaks.currentPointStreak || '-'} detail="Point streak" />
        <StatTile label="Shootout" value={player.shootoutAttempts > 0 ? `${player.shootoutGoals}/${player.shootoutAttempts}` : '-'} detail={player.shootoutAttempts > 0 ? `${formatPct(player.shootoutPct)} success` : 'No attempts'} />
      </section>

      <div className="grid grid--2">
        <ProfileCard title="Impact profile" icon={Flame}>
          <MetricList
            rows={[
              ['Game-winning goals', player.gameWinningGoals],
              ['Power-play goals', player.powerPlayGoals],
              ['Short-handed goals', player.shortHandedGoals],
              ['Late goals', player.lateGoals],
              ['Clutch goals', player.clutchGoals],
              ['Tying goals', player.tyingGoals],
              ['Go-ahead goals', player.goAheadGoals],
              ['Comeback goals', player.comebackGoals],
            ]}
          />
        </ProfileCard>

        <ProfileCard title="Game profile" icon={Trophy}>
          <MetricList
            rows={[
              ['Hat tricks', player.hatTricks],
              ['Multi-goal games', player.multiGoalGames],
              ['Multi-point games', player.multiPointGames],
              ['Games with a point', player.gamesWithPoint],
              ['Games without a point', player.gamesWithoutPoint],
              ['Longest point streak', player.streaks.longestPointStreak],
              ['Current point drought', player.streaks.currentPointDrought],
            ]}
          />
        </ProfileCard>
      </div>

      <div className="grid grid--2">
        <ProfileCard title="Best game" icon={Target}>
          {player.bestGame ? (
            <div className="public-profile__best">
              <strong>{player.bestGame.goals}G {player.bestGame.assists}A</strong>
              <p>{player.bestGame.points} points, {player.bestGame.shots} shots, {player.bestGame.penaltyMinutes} PIM</p>
              <Link className="btn btn--ghost btn--sm" to={`/events/${tournament.slug}/games/${player.bestGame.gameId}`}>
                Open game
              </Link>
            </div>
          ) : (
            <p className="muted">Best-game details appear once the player has a scored game.</p>
          )}
        </ProfileCard>

        <ProfileCard title="Assist chemistry" icon={Users}>
          {player.assistChemistry.length === 0 ? (
            <p className="muted">Assist combinations appear after connected scoring plays.</p>
          ) : (
            <div className="public-profile__list">
              {player.assistChemistry.slice(0, 6).map((row) => (
                <Link key={row.playerId} to={`/events/${tournament.slug}/players/${row.playerId}`} className="public-profile__row-link">
                  <span>{row.playerName}</span>
                  <strong>{row.totalCombinations}</strong>
                </Link>
              ))}
            </div>
          )}
        </ProfileCard>
      </div>

      <div className="grid grid--2">
        <PeriodCard player={player} />
        <GameLogCard player={player} slug={tournament.slug} />
      </div>
    </div>
    </PublicEventSection>
  );
}

function PeriodCard({ player }: { player: PlayerAnalyticsSummary }) {
  return (
    <ProfileCard title="Scoring by period" icon={Activity}>
      {player.scoringByPeriod.length === 0 ? (
        <p className="muted">Period splits appear as goals, assists, shots, and penalties are recorded.</p>
      ) : (
        <div className="table-scroll">
          <table className="table public-stats__table">
            <thead>
              <tr><th>Period</th><th>G</th><th>A</th><th>SOG</th><th>PIM</th><th>PPG</th><th>SHG</th></tr>
            </thead>
            <tbody>
              {player.scoringByPeriod.map((row) => (
                <tr key={row.period}>
                  <td>P{row.period}</td>
                  <td>{row.goals}</td>
                  <td>{row.assists}</td>
                  <td>{row.shots}</td>
                  <td>{row.penaltyMinutes}</td>
                  <td>{row.powerPlayGoals}</td>
                  <td>{row.shortHandedGoals}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </ProfileCard>
  );
}

function GameLogCard({ player, slug }: { player: PlayerAnalyticsSummary; slug: string }) {
  return (
    <ProfileCard title="Game log" icon={Trophy}>
      {player.gameLog.length === 0 ? (
        <p className="muted">Game log appears once the player has eligible completed games.</p>
      ) : (
        <div className="table-scroll">
          <table className="table public-stats__table">
            <thead>
              <tr><th>Game</th><th>Result</th><th>G</th><th>A</th><th>PTS</th><th>SOG</th><th>PIM</th><th>SO</th></tr>
            </thead>
            <tbody>
              {player.gameLog.map((row, index) => (
                <tr key={row.gameId}>
                  <td><Link to={`/events/${slug}/games/${row.gameId}`}>Game {index + 1}</Link></td>
                  <td><span className="chip">{row.result}</span></td>
                  <td>{row.goals}</td>
                  <td>{row.assists}</td>
                  <td className="mono">{row.points}</td>
                  <td>{row.shots}</td>
                  <td>{row.penaltyMinutes}</td>
                  <td>{row.shootoutAttempts > 0 ? `${row.shootoutGoals}/${row.shootoutAttempts}` : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </ProfileCard>
  );
}

function ProfileCard({ title, icon: Icon, children }: { title: string; icon: LucideIcon; children: ReactNode }) {
  return (
    <section className="card public-profile__card">
      <div className="card__head">
        <h3 className="card__title">
          <Icon size={16} style={{ verticalAlign: -2, marginRight: 6 }} />
          {title}
        </h3>
      </div>
      {children}
    </section>
  );
}

function MetricList({ rows }: { rows: Array<[string, number]> }) {
  return (
    <div className="public-profile__metric-list">
      {rows.map(([label, value]) => (
        <div key={label}>
          <span>{label}</span>
          <strong>{value}</strong>
        </div>
      ))}
    </div>
  );
}

function StatTile({ label, value, detail }: { label: string; value: string | number; detail: string }) {
  return (
    <div className="public-stats__kpi">
      <span>{label}</span>
      <strong>{value}</strong>
      <p>{detail}</p>
    </div>
  );
}

function formatNumber(value: number): string {
  if (!Number.isFinite(value)) return '0';
  return value % 1 === 0 ? String(value) : value.toFixed(1);
}

function formatPct(value: number): string {
  if (!Number.isFinite(value)) return '-';
  return `${Math.round(value * 100)}%`;
}
